import * as THREE from 'three';
import {
  DEFAULT_SCAN_VERTEX_UNIT,
  fileUnitToSceneCmMultiplier,
  resolveScanVertexUnit,
} from './vertexScaleToScene';

/**
 * Utility class for scaling 3D models to maintain real-world scale
 * Target: 1 cm in real life = 1 unit in Three.js scene (scan / socket)
 */

/** Multiplier file unit → cm scene @see fileUnitToSceneCmMultiplier */
export const UNIT_SCALES = {
  mm: fileUnitToSceneCmMultiplier('mm'),
  cm: fileUnitToSceneCmMultiplier('cm'),
  m: fileUnitToSceneCmMultiplier('m'),
  inches: fileUnitToSceneCmMultiplier('inches', { allowInches: true }),
  auto: null,
};

/**
 * Calculate the bounding box of a mesh
 * @param {THREE.Mesh} mesh - The mesh to measure
 * @returns {THREE.Box3} The bounding box
 */
export function calculateBoundingBox(mesh) {
  const boundingBox = new THREE.Box3().setFromObject(mesh);
  return boundingBox;
}

/**
 * Get dimensions from a bounding box
 * @param {THREE.Box3} boundingBox
 * @returns {{width: number, height: number, depth: number, max: number}}
 */
export function getBoundingBoxDimensions(boundingBox) {
  const size = new THREE.Vector3();
  boundingBox.getSize(size);

  return {
    width: size.x,
    height: size.y,
    depth: size.z,
    max: Math.max(size.x, size.y, size.z),
  };
}

/**
 * Optional bbox heuristic when UI explicitly requests `auto` (BK scans are usually mm).
 *
 * @param {THREE.Box3} boundingBox
 * @returns {{detectedUnit: string, confidence: string, scaleFactor: number, reasoning: string}}
 */
export function detectUnits(boundingBox) {
  const dimensions = getBoundingBoxDimensions(boundingBox);
  const largestDim = dimensions.max;

  // Real socket ~15–45 cm. In file units: ~0.15–0.45 m, ~15–45 cm, ~150–450 mm.
  if (largestDim < 1.5) {
    console.log('Largest dimension is less than 1.5 meters');
    return {
      detectedUnit: 'm',
      confidence: 'high',
      scaleFactor: UNIT_SCALES.m,
      reasoning: `Largest dimension (${largestDim.toFixed(1)}) < 1.5 suggests meters`,
    };
  }

  if (largestDim >= 12 && largestDim <= 55) {
    console.log('Largest dimension is between 12 and 55 centimeters');
    return {
      detectedUnit: 'cm',
      confidence: 'medium',
      scaleFactor: UNIT_SCALES.cm,
      reasoning: `Largest dimension (${largestDim.toFixed(1)}) in 12–55 suggests centimeters`,
    };
  }

  if (largestDim >= 150) {
    console.log('Largest dimension is greater than 150 millimeters');
    return {
      detectedUnit: 'mm',
      confidence: 'high',
      scaleFactor: UNIT_SCALES.mm,
      reasoning: `Largest dimension (${largestDim.toFixed(1)}) >= 150 suggests millimeters`,
    };
  }

  return {
    detectedUnit: DEFAULT_SCAN_VERTEX_UNIT,
    confidence: 'medium',
    scaleFactor: UNIT_SCALES.mm,
    reasoning: `Defaulting to ${DEFAULT_SCAN_VERTEX_UNIT} (typical BK scan; largest dim ${largestDim.toFixed(1)})`,
  };
}

/**
 * Apply scaling to a mesh based on source file unit → cm scene.
 *
 * @param {THREE.Mesh} mesh
 * @param {string} sourceUnit
 * @returns {{scaleFactor: number, dimensions: object}}
 */
export function applyScaleForUnit(mesh, sourceUnit) {
  const scaleFactor = fileUnitToSceneCmMultiplier(sourceUnit, { allowInches: true });

  if (!Number.isFinite(scaleFactor) || scaleFactor <= 0) {
    console.warn(`Invalid unit specified for scene scale: ${sourceUnit}`);
    return { scaleFactor: 1.0, dimensions: getBoundingBoxDimensions(calculateBoundingBox(mesh)) };
  }

  mesh.geometry.scale(scaleFactor, scaleFactor, scaleFactor);
  mesh.geometry.computeBoundingSphere();

  const scaledBox = calculateBoundingBox(mesh);
  const dimensions = getBoundingBoxDimensions(scaledBox);

  return { scaleFactor, dimensions };
}

/**
 * Apply a manual scale factor to a mesh
 * @param {THREE.Mesh} mesh
 * @param {number} scaleFactor
 * @returns {{dimensions: object}}
 */
export function applyManualScale(mesh, scaleFactor) {
  mesh.geometry.scale(scaleFactor, scaleFactor, scaleFactor);
  mesh.geometry.computeBoundingSphere();

  const scaledBox = calculateBoundingBox(mesh);
  const dimensions = getBoundingBoxDimensions(scaledBox);

  return { dimensions };
}

/**
 * Center a mesh at the origin (baked into geometry).
 * @param {THREE.Mesh} mesh
 */
export function centerModel(mesh) {
  const boundingBox = calculateBoundingBox(mesh);
  const center = new THREE.Vector3();
  boundingBox.getCenter(center);

  mesh.geometry.translate(-center.x, -center.y, -center.z);
  mesh.geometry.computeBoundingSphere();
}

/**
 * Bake scan mesh from file units into centimeter scene space.
 *
 * @param {THREE.Mesh} mesh
 * @param {string|null} manualUnit - `auto` | `mm` | `cm` | `m` | `inches` | null → BK default mm
 * @param {number} manualScaleFactor
 */
export function scaleModelToCentimeters(mesh, manualUnit = null, manualScaleFactor = 1.0) {
  const originalBox = calculateBoundingBox(mesh);
  const originalDimensions = getBoundingBoxDimensions(originalBox);

  console.log('📏 Original model dimensions:', originalDimensions);

  let unitInfo;
  if (manualUnit === 'auto') {
    unitInfo = detectUnits(originalBox);
  } else {
    const resolved = resolveScanVertexUnit(manualUnit);
    unitInfo = {
      detectedUnit: resolved,
      confidence: manualUnit ? 'manual' : 'default',
      scaleFactor: UNIT_SCALES[resolved],
      reasoning:
        manualUnit && manualUnit !== 'auto'
          ? `Manually specified unit: ${manualUnit}`
          : `BK default scan unit: ${DEFAULT_SCAN_VERTEX_UNIT}`,
    };
  }

  console.log(`🔍 Unit: ${unitInfo.detectedUnit} (${unitInfo.confidence})`);
  console.log(`   ${unitInfo.reasoning}`);

  const { scaleFactor: unitScaleFactor, dimensions: scaledDimensions } = applyScaleForUnit(
    mesh,
    unitInfo.detectedUnit
  );

  console.log(`⚖️  Applied scale factor: ${unitScaleFactor.toFixed(3)}`);
  console.log('📏 Scaled dimensions (cm scene):', scaledDimensions);

  if (manualScaleFactor !== 1.0) {
    applyManualScale(mesh, manualScaleFactor);
    console.log(`🔧 Applied additional manual scale: ${manualScaleFactor.toFixed(3)}`);
  }

  centerModel(mesh);
  console.log('✅ Model centered at origin');

  const finalBox = calculateBoundingBox(mesh);
  const finalDimensions = getBoundingBoxDimensions(finalBox);

  return {
    detectedUnit: unitInfo.detectedUnit,
    confidence: unitInfo.confidence,
    scaleFactor: unitScaleFactor * manualScaleFactor,
    dimensions: finalDimensions,
    reasoning: unitInfo.reasoning,
    originalDimensions,
  };
}
